import {Account} from '../types';
import {
    ActionIcon,
    Anchor,
    Button,
    Card,
    CopyButton,
    Divider,
    Group,
    Paper,
    SimpleGrid,
    Stack,
    Text,
    Title,
    Tooltip,
} from '@mantine/core';
import {AiOutlineCheck, AiOutlineCopy, AiOutlineCreditCard, AiOutlineIdcard} from 'react-icons/ai';
import {BiTime} from 'react-icons/bi';
import {AccountStatusBadge} from './AccountStatusBadge';
import {AccountTypeBadge} from './AccountTypeBadge';
import {useBillingSetup} from '../../../billing-management/customers/hooks/useBillingSetup';
import {Link} from 'react-router-dom';

const CopyIcon = ({value}: { value: string }) => (
    <CopyButton value={value} timeout={2000}>
        {({copied, copy}) => (
            <Tooltip label={copied ? 'Copied' : 'Copy'} withArrow>
                <ActionIcon
                    variant="subtle"
                    color={copied ? 'teal' : 'gray'}
                    onClick={copy}
                    size="sm"
                    aria-label={`Copy ${value}`}
                >
                    {copied ? <AiOutlineCheck size={14}/> : <AiOutlineCopy size={14}/>}
                </ActionIcon>
            </Tooltip>
        )}
    </CopyButton>
);

const InfoRow = ({label, children}: { label: string; children: React.ReactNode }) => (
    <Group justify="space-between" wrap="nowrap">
        <Text size="sm" c="dimmed" style={{minWidth: 120}}>
            {label}
        </Text>
        <Group gap="xs" wrap="nowrap">
            {children}
        </Group>
    </Group>
);

const formatDate = (iso?: string) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });
};

export const Profile = ({record, showDangerZone = false}: { record: Account; showDangerZone?: boolean }) => {
    const {setupBilling, isSettingUp} = useBillingSetup();

    return (
        <Stack gap="lg">
            <Text c="dimmed" size="sm">
                Your account ID, status, type, and billing configuration. These are
                system-level settings managed by the platform.
            </Text>

            {/* Account Header */}
            <Card shadow="sm" padding="lg" radius="md" withBorder>
                <Group justify="space-between" wrap="nowrap">
                    <Stack gap={4}>
                        <Group gap="xs">
                            <Text size="sm" c="dimmed">Account ID:</Text>
                            <Text size="sm" ff="monospace">{record.id}</Text>
                            <CopyIcon value={record.id}/>
                        </Group>
                    </Stack>
                    <Group gap="xs">
                        <AccountStatusBadge record={record}/>
                        <AccountTypeBadge record={record}/>
                    </Group>
                </Group>
            </Card>

            {/* Account Details */}
            <Card shadow="sm" padding="lg" radius="md" withBorder>
                <Group gap="xs" mb="md">
                    <AiOutlineIdcard size={18}/>
                    <Title order={5}>Account Details</Title>
                </Group>
                <Stack gap="sm">
                    <InfoRow label="Username">
                        <Text size="sm">{record.username || '-'}</Text>
                    </InfoRow>
                    <Divider/>
                    <InfoRow label="Email">
                        <Text size="sm">{record.email}</Text>
                    </InfoRow>
                    <Divider/>
                    <SimpleGrid cols={2}>
                        <Group gap="xs">
                            <BiTime size={14} color="var(--mantine-color-dimmed)"/>
                            <Text size="xs" c="dimmed">
                                Member since {formatDate(record.created)}
                            </Text>
                        </Group>
                        <Group gap="xs">
                            <BiTime size={14} color="var(--mantine-color-dimmed)"/>
                            <Text size="xs" c="dimmed">
                                Last updated {formatDate(record.updated)}
                            </Text>
                        </Group>
                    </SimpleGrid>
                </Stack>
            </Card>

            {/* Billing */}
            <Card shadow="sm" padding="lg" radius="md" withBorder>
                <Group gap="xs" mb="md">
                    <AiOutlineCreditCard size={18}/>
                    <Title order={5}>Billing</Title>
                </Group>
                {record.customerId ? (
                    <InfoRow label="Customer ID">
                        <Text size="sm" ff="monospace" fw={700}>{record.customerId}</Text>
                        <CopyIcon value={record.customerId}/>
                    </InfoRow>
                ) : (
                    <Paper p="md" radius="sm" bg="var(--mantine-color-blue-light)">
                        <Group justify="space-between" wrap="nowrap">
                            <Group gap="sm" wrap="nowrap">
                                <AiOutlineCreditCard size={20}/>
                                <Text size="sm">
                                    Set up billing to enable payments and invoicing.
                                </Text>
                            </Group>
                            <Button
                                size="compact-sm"
                                variant="light"
                                onClick={setupBilling}
                                loading={isSettingUp}
                            >
                                Set Up Billing
                            </Button>
                        </Group>
                    </Paper>
                )}
            </Card>

            <Text size="xs" c="dimmed" ta="center" mt="md">
                Need to update your name, email, or contact details?{' '}
                <Anchor component={Link} to="/user-profile" size="xs">
                    Go to Personal Info
                </Anchor>
            </Text>

            {/* Danger Zone */}
            {showDangerZone && (
                <Card shadow="sm" padding="lg" radius="md" withBorder>
                    <Title order={5} mb="md" c="red">Danger Zone</Title>
                    <Paper p="md" radius="sm" bg="var(--mantine-color-red-light)">
                        <Group justify="space-between" wrap="nowrap">
                            <Stack gap={2}>
                                <Text size="sm" fw={500}>Delete your account</Text>
                                <Text size="xs" c="dimmed">
                                    After requesting deletion, you have 30 days to recover by contacting support. After that, all data is permanently removed.
                                </Text>
                            </Stack>
                            <Button
                                component={Link}
                                to="/account/delete-account"
                                variant="outline"
                                color="red"
                                size="compact-sm"
                            >
                                Delete Account
                            </Button>
                        </Group>
                    </Paper>
                </Card>
            )}
        </Stack>
    );
};
